import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Droplets, Repeat, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import { API } from "../lib/constants";
import { Logo } from "../components/Logo";

export const SubscriptionCancel = () => {
  const { subscriptionId: paramId } = useParams();
  const [code, setCode] = useState(paramId || "");
  const [info, setInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const loadInfo = useCallback(async (id) => {
    setError("");
    setIsLoading(true);
    setInfo(null);
    setConfirmed(false);
    try {
      const res = await axios.get(`${API}/subscription/${id.trim()}`);
      setInfo(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (paramId) loadInfo(paramId);
  }, [paramId, loadInfo]);

  const cancel = async () => {
    if (!info) return;
    setIsCancelling(true);
    try {
      const res = await axios.delete(`${API}/subscription/${info.subscription_id}`);
      toast.success(res.data.message || "Abbonamento disdetto");
      setConfirmed(true);
      loadInfo(info.subscription_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella disdetta");
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="min-h-screen gradient-hero px-4 py-12" data-testid="subscription-cancel-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-800 mb-4" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4" /> Torna alla home
          </Link>
          <div className="flex items-center justify-center gap-2 mb-4">
            <Logo size={44} />
            <span className="font-bold text-2xl text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Acqua Vicina
            </span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Gestisci il tuo Abbonamento
          </h1>
        </div>

        <div className="booking-card rounded-3xl p-8">
          {!info && (
            <div className="space-y-4">
              <Label className="text-slate-700 font-medium block">
                Inserisci il codice abbonamento ricevuto via email
              </Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Es: 780cb742-3d56-4a53-9183-..."
                className="h-12 rounded-xl border-slate-200"
                data-testid="subscription-code-input"
              />
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2" data-testid="subscription-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                </div>
              )}
              <Button
                onClick={() => loadInfo(code)}
                disabled={isLoading || !code.trim()}
                className="w-full btn-primary text-white rounded-full h-12"
                data-testid="load-subscription-btn"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Cerca Abbonamento"
                )}
              </Button>
            </div>
          )}

          {info && (
            <div className="space-y-6" data-testid="subscription-info">
              <div className="flex items-start gap-3 p-4 bg-sky-50 rounded-2xl">
                <Repeat className="w-6 h-6 text-sky-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-800">Abbonamento {info.nome}</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Frequenza: <strong className="capitalize">{info.ricorrenza}</strong>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <p className="text-2xl font-bold text-slate-900" data-testid="stat-totale">{info.totale_prenotazioni}</p>
                  <p className="text-xs text-slate-500 mt-1">Totale ordini</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <p className="text-2xl font-bold text-emerald-600" data-testid="stat-attive">{info.prenotazioni_attive}</p>
                  <p className="text-xs text-slate-500 mt-1">Attivi</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <p className="text-2xl font-bold text-sky-600" data-testid="stat-prossime">{info.prossime_consegne}</p>
                  <p className="text-xs text-slate-500 mt-1">Future</p>
                </div>
              </div>

              {(() => {
                if (confirmed || info.annullato) {
                  return (
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center gap-3" data-testid="subscription-cancelled">
                      <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                      <div>
                        <p className="font-semibold text-emerald-900">Abbonamento disdetto</p>
                        <p className="text-sm text-emerald-700">
                          Le tue future consegne sono state annullate. Le consegne passate restano consultabili.
                        </p>
                      </div>
                    </div>
                  );
                }
                if (info.prossime_consegne === 0) {
                  return (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-sm text-amber-800" data-testid="no-future">
                      Non ci sono consegne future da disdire per questo abbonamento.
                    </div>
                  );
                }
                return (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-600">
                      Confermando la disdetta, tutte le {info.prossime_consegne} consegne future verranno annullate.
                      Le consegne già completate o passate non saranno toccate.
                    </p>
                    <Button
                      onClick={cancel}
                      disabled={isCancelling}
                      variant="destructive"
                      className="w-full rounded-full h-12"
                      data-testid="confirm-cancel-subscription-btn"
                    >
                      {isCancelling ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        "Disdici Abbonamento"
                      )}
                    </Button>
                  </div>
                );
              })()}

              <Button
                variant="outline"
                onClick={() => { setInfo(null); setCode(""); setError(""); }}
                className="w-full rounded-full"
                data-testid="search-other-btn"
              >
                Cerca un altro abbonamento
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
